import Api from '../Api';
import {
  fetchOrganizationAutofillCitiesData,
  stripJsonFromPastedText,
  normalizeContactDigits,
  findCityById,
} from './organizationAutofill';
import {
  parseHoldingDatesListForForm,
  isValidHoldingTime,
} from './formatHoldingDatesList';

const CONTACT_KEYS = ['phone_number', 'instagram', 'telegram', 'whatsapp', 'viber', 'facebook', 'website'];
const PHONE_DIGIT_CONTACT_KEYS = ['phone_number', 'whatsapp', 'viber'];

/**
 * Общий файл (без городов): только категории событий.
 */
export async function fetchEventAutofillCommonData() {
  const { data } = await Api.getEventCategories();
  return { eventCategories: data?.categories || [] };
}

/**
 * Данные для «Преобразовать»: город выбранной страны + категории событий.
 */
export async function fetchEventAutofillDataForSelectedCountry(countryId) {
  const [citiesPart, { eventCategories }] = await Promise.all([
    fetchOrganizationAutofillCitiesData(countryId),
    fetchEventAutofillCommonData(),
  ]);
  const { selectedCountry, cities } = citiesPart;
  return {
    eventCategories,
    selectedCountry,
    citiesByCountryId: { [selectedCountry._id]: cities },
  };
}

function findEventCategoryById(eventCategories, id) {
  if (!id) return null;
  return eventCategories.find((c) => c._id === id) || null;
}

function normalizeHoldingTimeValue(value) {
  if (value == null) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  if (isValidHoldingTime(raw)) return raw;
  const match = raw.match(/^([01]?\d|2[0-3])[:.]([0-5]\d)$/);
  if (!match) return '';
  return `${match[1].padStart(2, '0')}:${match[2]}`;
}

/**
 * Частичное заполнение формы «Добавить мероприятие».
 */
export function parseEventAutofillJson(jsonString, staticData) {
  let data;
  try {
    data = JSON.parse(stripJsonFromPastedText(jsonString));
  } catch (e) {
    return { error: 'Не удалось разобрать JSON. Проверьте скобки и кавычки.' };
  }

  if (!data || typeof data !== 'object') {
    return { error: 'Корень JSON должен быть объектом.' };
  }

  const has = (k) => Object.prototype.hasOwnProperty.call(data, k);
  /** Ключ есть и значение не null — null значит «поле не трогать, не валидировать» */
  const take = (k) => has(k) && data[k] !== null;
  const { citiesByCountryId, eventCategories } = staticData;

  const patches = {};

  if (take('owner_id')) patches.ownerId = String(data.owner_id).trim();
  if (take('name')) patches.name = String(data.name).trim();

  if (take('holding_dates_list')) {
    if (!Array.isArray(data.holding_dates_list) || data.holding_dates_list.length === 0) {
      return { error: 'holding_dates_list должен быть непустым массивом дат (YYYY-MM-DD или ISO).' };
    }
    const parsed = parseHoldingDatesListForForm(data.holding_dates_list);
    if (!parsed.dates.length || !parsed.dates[0]) {
      return { error: 'holding_dates_list: не удалось разобрать ни одной даты.' };
    }
    patches.holdingDates = parsed.dates;

    const explicitTime = take('holding_time')
      ? normalizeHoldingTimeValue(data.holding_time)
      : '';
    patches.holdingTime = explicitTime || parsed.time;

    if (!take('date_start') && parsed.dates[0]) {
      patches.dateStart = parsed.dates[0];
    }
    if (!take('date_end') && parsed.dates.length) {
      patches.dateEnd = parsed.dates[parsed.dates.length - 1];
    }
  } else if (take('holding_date')) {
    return {
      error: 'Поле holding_date устарело. Используйте holding_dates_list: массив дат YYYY-MM-DD (и опционально holding_time HH:MM).',
    };
  } else if (take('holding_time')) {
    patches.holdingTime = normalizeHoldingTimeValue(data.holding_time);
  }

  if (take('date_start')) patches.dateStart = String(data.date_start).trim();
  if (take('date_end')) patches.dateEnd = String(data.date_end).trim();
  if (take('address')) patches.address = String(data.address).trim();
  if (take('description')) patches.description = String(data.description).trim();

  if (take('contacts') && data.contacts && typeof data.contacts === 'object') {
    patches.contactsPartial = {};
    CONTACT_KEYS.forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(data.contacts, k) && data.contacts[k] !== null) {
        const v = data.contacts[k];
        const raw = v != null ? String(v).trim() : '';
        patches.contactsPartial[k] = PHONE_DIGIT_CONTACT_KEYS.includes(k)
          ? normalizeContactDigits(raw)
          : raw;
      }
    });
  }

  if (take('events_category_id')) {
    const cat = findEventCategoryById(eventCategories, data.events_category_id);
    if (!cat) {
      return { error: 'Категория мероприятия не найдена по events_category_id.' };
    }
    patches.eventCategory = cat;
  }

  if (take('city_id')) {
    const city = findCityById(citiesByCountryId, data.city_id);
    if (!city) {
      return { error: 'Город не найден по city_id.' };
    }
    patches.city = city;
  }

  if (take('coordinates') && data.coordinates && (data.coordinates.lat != null || data.coordinates.lon != null)) {
    patches.coordinates = {
      lat: data.coordinates.lat != null ? String(data.coordinates.lat) : '',
      lon: data.coordinates.lon != null ? String(data.coordinates.lon) : '',
      is_special_point_on_map: Boolean(data.coordinates.is_special_point_on_map),
    };
  }

  return { patches };
}

function buildEventAutofillSchemaDescription() {
  return {
    description: 'Один JSON-объект для формы «Добавить мероприятие». В одном сообщении два фрагмента: общий файл (REFERENCE_COMMON: event_categories) и файл городов страны (REFERENCE_CITIES). city_id только из REFERENCE_CITIES.cities; events_category_id из REFERENCE_COMMON.event_categories.',
    fields: {
      owner_id: '24-символьный hex ID владельца',
      name: 'Название мероприятия',
      events_category_id: 'строка — _id из REFERENCE_COMMON.event_categories',
      holding_dates_list: 'обязательный массив дат YYYY-MM-DD (или ISO), минимум одна; пример: ["2026-12-22","2026-12-23"]',
      holding_time: 'необязательно, HH:MM — одно общее время на все дни; если у дней разное время — не заполняй, опиши в description',
      date_start: 'YYYY-MM-DD или пусто; если пусто — можно первую дату из holding_dates_list',
      date_end: 'YYYY-MM-DD или пусто; если пусто — можно последнюю дату из holding_dates_list',
      city_id: 'строка — cities[i]._id из REFERENCE_CITIES.cities (страна в REFERENCE_CITIES.selected_country)',
      address: 'строка',
      coordinates: { lat: 'число или строка', lon: 'число или строка', is_special_point_on_map: false },
      contacts: 'phone_number, whatsapp, viber — только цифры подряд; остальные поля — строки',
      description: 'строка',
    },
    do_not_use: {
      holding_date: 'устарело — вместо него только holding_dates_list',
      specialization: 'поле больше не используется для мероприятий',
    },
    expected_response_example: {
      owner_id: '000000000000000000000001',
      name: 'Концерт',
      events_category_id: '0000000000000000000000aa',
      holding_dates_list: ['2026-12-22', '2026-12-23'],
      holding_time: '19:00',
      date_start: '2026-12-22',
      date_end: '2026-12-23',
      city_id: '0000000000000000000000bb',
      address: 'ул. Примерная, 1',
      coordinates: { lat: '40.18', lon: '44.51', is_special_point_on_map: false },
      contacts: {
        phone_number: '37499111222',
        instagram: '',
        telegram: '',
        whatsapp: '',
        viber: '',
        facebook: '',
        website: '',
      },
      description: 'Краткое описание',
    },
    json_syntax: 'Ответ только markdown-блок ```json … ```, внутри один объект после JSON.stringify. Экранирование \\" для кавычек внутри строк.',
    note: 'Телефоны в contacts.phone_number, whatsapp, viber — только цифры. Даты проведения — только holding_dates_list (+ опционально holding_time).',
  };
}

function eventAutofillPromptModelLines() {
  const schemaDescription = buildEventAutofillSchemaDescription();
  return [
    'Ты — помощник по заполнению карточки мероприятия в админке Nomad.',
    '',
    'Порядок работы:',
    '— Сначала в ОДНОМ сообщении приходят два фрагмента подряд: (А) общий файл без городов — event_categories как REFERENCE_COMMON; (Б) файл REFERENCE_CITIES с selected_country и cities. На это сообщение ты НЕ генерируешь JSON.',
    '— Если задача понятна, ответь: «Задача ясна, отправь текст для преобразования».',
    '— Когда пришлют текст о мероприятии, сформируй ответ по схеме.',
    '',
    'REFERENCE_COMMON.event_categories — глобальные категории афиш. REFERENCE_CITIES — город и страна только для city_id.',
    '',
    'КРИТИЧНО — категория (events_category_id):',
    '— Только _id из REFERENCE_COMMON.event_categories.',
    '',
    'КРИТИЧНО — телефоны (contacts.phone_number, contacts.whatsapp, contacts.viber):',
    '— Только цифры; +7 … → только цифры.',
    '— instagram, telegram, facebook, website не режь до цифр.',
    '',
    'КРИТИЧНО — даты проведения (новый формат):',
    '— Обязателен holding_dates_list: массив строк YYYY-MM-DD (или ISO), минимум одна дата.',
    '— Пример: "holding_dates_list": ["2026-02-02","2026-02-07","2026-02-20"].',
    '— holding_time опционально (HH:MM): одно время на ВСЕ дни.',
    '— Если у дней разное время — holding_time не заполняй, распиши в description.',
    '— НЕ используй устаревшее поле holding_date (строка).',
    '— НЕ используй specialization — поле удалено из мероприятий.',
    '— date_start / date_end: YYYY-MM-DD или пустая строка; если не указаны — возьми первую и последнюю из holding_dates_list.',
    '',
    'КРИТИЧНО — внутренние двойные кавычки в тексте:',
    '— Внутри строковых значений экранируй " как \\".',
    '',
    'КРИТИЧНО — город city_id:',
    '— Только _id из REFERENCE_CITIES.cities.',
    '',
    'КРИТИЧНО — формат ответа:',
    '— Всегда возвращай результат ТОЛЬКО внутри блока кода с типом json:',
    '```json',
    '{...}',
    '```',
    '— Без текста до ```json и после ```.',
    '— Один объект для JSON.parse; кавычки в строках только \\".',
    '',
    'Когда пришёл текст для преобразования:',
    '1) Заполни поля из schemaDescription.fields (ориентир — expected_response_example).',
    '2) events_category_id — REFERENCE_COMMON; city_id — REFERENCE_CITIES.',
    '3) Обязательно заполни holding_dates_list; holding_date и specialization не пиши.',
    '4) Ответ одним блоком ```json … ```.',
    '',
    'SCHEMA_HINT:',
    JSON.stringify(schemaDescription, null, 2),
    '',
    'REFERENCE_COMMON:',
  ];
}

/** Файл 1: категории мероприятий без городов. */
export function buildEventAutofillCommonFileContent({ eventCategories }) {
  const ec = eventCategories
    .slice()
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
    .map((c) => ({ _id: c._id, name: c.name, sort: c.sort }));
  const referenceCommon = { event_categories: ec };
  const prompt = [...eventAutofillPromptModelLines(), JSON.stringify(referenceCommon, null, 2)].join('\n');

  return [
    '══════════════════════════════════════════════════════════════',
    'NOMAD — автозаполнение мероприятия: ОБЩИЙ файл (без городов)',
    '══════════════════════════════════════════════════════════════',
    '',
    'Как пользоваться (оператору):',
    '1) Общий файл один для всех стран (только категории событий). Второй — города страны — скачивать отдельно для каждой страны.',
    '2) Первым сообщением вставьте в чат модели общий файл целиком, сразу за ним (в том же сообщении) файл городов нужной страны.',
    '3) Дождитесь от модели: «Задача ясна, отправь текст для преобразования».',
    '4) Пришлите текст о мероприятии.',
    '5) Ответ модели с блоком ```json можно вставить в поле ниже; в админке выберите ту же страну, что во втором файле — «Преобразовать».',
    '6) Если JSON отклонён системой — https://jsonviewer.stack.hu/, «Format», исправление по подсказкам.',
    '7) В JSON даты — holding_dates_list (массив), не holding_date. specialization не нужен.',
    '',
    prompt,
  ].join('\n');
}

/** Файл 2: города страны для мероприятий. */
export function buildEventAutofillCitiesFileContent({ selectedCountry, cities }) {
  if (!selectedCountry?._id) {
    throw new Error('selectedCountry is required');
  }
  const countryId = selectedCountry._id;
  const referenceCities = {
    selected_country: { _id: selectedCountry._id, name: selectedCountry.name },
    cities: (cities || []).map((city) => ({
      _id: city._id,
      name: city.name,
      country_id: city.country_id || countryId,
    })),
    note_model: `Города для мероприятий (${selectedCountry.name}). Категории — в общем файле.`,
  };

  return [
    '══════════════════════════════════════════════════════════════',
    `NOMAD — автозаполнение мероприятия: ГОРОДА — ${selectedCountry.name}`,
    '══════════════════════════════════════════════════════════════',
    '',
    'Как пользоваться (оператору):',
    '— Приложение к общему файлу мероприятий. Для каждой страны — свой такой файл. Оба отправляются вместе одним сообщением.',
    '',
    'REFERENCE_CITIES:',
    JSON.stringify(referenceCities, null, 2),
  ].join('\n');
}
